import { Columns3, MoreHorizontal, Plus, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ProjectSelector } from "./ProjectSelector";
import type { WorkspaceProject } from "@/hooks/useWorkspaceProjects";

type TasksTab = "minhas" | "equipe" | "metrics";

interface TasksProjectBarProps {
  projects: WorkspaceProject[];
  activeProjectId: string | null;
  onSelectProject: (id: string | null) => void;
  onOpenProjectManager: () => void;
  onOpenColumnManager: () => void;
  onNewTask: () => void;
  projectsLoading?: boolean;
  isAdmin?: boolean;
  orphanCount?: number;
  showColumnManager?: boolean;
  canViewTaskPerformance?: boolean;
  deletionRequestsCount?: number;
  tab: TasksTab;
  onTabChange: (value: string) => void;
}

const TAB_LABEL: Record<TasksTab, string> = {
  minhas: "Minhas",
  equipe: "Equipe",
  metrics: "Desempenho",
};

export function TasksProjectBar({
  projects,
  activeProjectId,
  onSelectProject,
  onOpenProjectManager,
  onOpenColumnManager,
  onNewTask,
  projectsLoading = false,
  isAdmin = false,
  orphanCount = 0,
  showColumnManager = false,
  canViewTaskPerformance = false,
  deletionRequestsCount = 0,
  tab,
  onTabChange,
}: TasksProjectBarProps) {
  const showColumns = isAdmin && showColumnManager && Boolean(activeProjectId);

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b pb-2 min-w-0 md:flex-row md:items-center md:gap-2 md:pb-3">
      <div className="flex items-center gap-1.5 min-w-0">
        <div className="min-w-0 flex-1">
          <ProjectSelector
            projects={projects}
            activeProjectId={activeProjectId}
            onSelectProject={onSelectProject}
            onOpenManager={onOpenProjectManager}
            loading={projectsLoading}
            isAdmin={isAdmin}
            orphanCount={orphanCount}
          />
        </div>

        {canViewTaskPerformance && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 shrink-0 md:hidden gap-1 px-2.5">
                {TAB_LABEL[tab]}
                {isAdmin && deletionRequestsCount > 0 && tab !== "equipe" && (
                  <Badge variant="destructive" className="h-4 min-w-4 px-1 text-[10px] leading-none">
                    {deletionRequestsCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => onTabChange("minhas")}>Minhas</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTabChange("equipe")}>
                Equipe
                {isAdmin && deletionRequestsCount > 0 ? ` (${deletionRequestsCount})` : ""}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTabChange("metrics")}>Desempenho</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 md:hidden"
              aria-label="Mais ações"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {showColumns && (
              <DropdownMenuItem onClick={onOpenColumnManager}>
                <Columns3 className="w-4 h-4 mr-2" />
                Colunas do quadro
              </DropdownMenuItem>
            )}
            {isAdmin && (
              <DropdownMenuItem onClick={onOpenProjectManager}>
                <Settings className="w-4 h-4 mr-2" />
                Projetos
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onNewTask}>
              <Plus className="w-4 h-4 mr-2" />
              Nova tarefa
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TabsList
        className={cn(
          "hidden md:flex h-9 shrink-0 p-1",
          canViewTaskPerformance && "grid grid-cols-3",
        )}
      >
        <TabsTrigger value="minhas" className="px-2.5 text-sm">
          Minhas
        </TabsTrigger>
        {canViewTaskPerformance && (
          <TabsTrigger value="equipe" className="px-2.5 text-sm gap-1">
            Equipe
            {isAdmin && deletionRequestsCount > 0 && (
              <Badge variant="destructive" className="h-4 min-w-4 px-1 text-[10px] leading-none">
                {deletionRequestsCount}
              </Badge>
            )}
          </TabsTrigger>
        )}
        {canViewTaskPerformance && (
          <TabsTrigger value="metrics" className="px-2 text-sm">
            Desempenho
          </TabsTrigger>
        )}
      </TabsList>

      <div className="hidden md:flex items-center gap-1 shrink-0 ml-auto">
        {showColumns && (
          <Button variant="ghost" size="sm" onClick={onOpenColumnManager}>
            <Columns3 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Colunas</span>
          </Button>
        )}
        {isAdmin && (
          <Button variant="ghost" size="sm" onClick={onOpenProjectManager}>
            <Settings className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Projetos</span>
          </Button>
        )}
        <Button size="sm" onClick={onNewTask}>
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Nova tarefa</span>
        </Button>
      </div>
    </div>
  );
}
